import React, { Suspense } from 'react';
import { motion } from 'framer-motion';
import { MetricCard } from "./MetricCard";
import { NewsCard } from './NewsCard';
import { LeaksCard } from './LeaksCard';
import { EOLCard } from './EOLCard';
import { ReleasesCard } from './ReleasesCard';
import { VulnerabilitiesCard } from './VulnerabilitiesCard';
import { RansomwareCard } from './RansomwareCard';
import { useLanguage } from "@/hooks/useLanguage";
import { Lock, Database, Globe, AlertOctagon, AlertCircle, AlertTriangle, TrendingUp } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";

// Lazy load heavy components
const RansomwareMapWidget = React.lazy(() => import('./RansomwareMapWidget').then(module => ({ default: module.RansomwareMapWidget })));

interface DashboardCardsProps {
  data: any;
  isLoading: boolean;
  days: number;
}

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const item = {
  hidden: { y: 20, opacity: 0 },
  show: { y: 0, opacity: 1 }
};

export function DashboardCards({ data, isLoading, days }: DashboardCardsProps) {
  const { t } = useLanguage();
  const timeLabel = t('dashboard.last_days').replace('{days}', days.toString());

  return (
    <motion.div
      variants={container}
      initial="hidden"
      animate="show"
      className="space-y-6"
    >
      {/* Top Metrics Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <motion.div variants={item}>
          <MetricCard
            icon={<Lock className="h-6 w-6" />}
            title={t('dashboard.ransomware_victims')}
            value={isLoading ? undefined : data?.ransomwareVictimsCount}
            description={timeLabel}
            gradient="bg-gradient-to-br from-cyan-500/20 to-blue-500/20 hover:from-cyan-500/30 hover:to-blue-500/30 border-cyan-500/50"
            textColor="text-cyan-500"
            linkTo="/ransomware"
          />
        </motion.div>
        <motion.div variants={item}>
          <MetricCard
            icon={<Database className="h-6 w-6" />}
            title={t('dashboard.data_leaks')}
            value={isLoading ? undefined : data?.leaksCount}
            description={timeLabel}
            gradient="bg-gradient-to-br from-indigo-500/20 via-purple-500/20 to-pink-500/20 hover:from-indigo-500/30 hover:via-purple-500/30 hover:to-pink-500/30 border-purple-500/50"
            textColor="text-purple-500"
            linkTo="/leaks"
          />
        </motion.div>
        <motion.div variants={item}>
          <MetricCard
            icon={<Lock className="h-6 w-6" />}
            title={t('dashboard.active_group')}
            value={isLoading ? undefined : data?.mostActiveRansomwareGroup}
            description={timeLabel}
            gradient="bg-gradient-to-br from-rose-500/20 to-orange-500/20 hover:from-rose-500/30 hover:to-orange-500/30 border-rose-500/50"
            textColor="text-rose-500"
            isText={true}
            linkTo={data?.mostActiveRansomwareGroup ? `/ransomware/group/${data.mostActiveRansomwareGroup}` : "/ransomware"}
          />
        </motion.div>
        <motion.div variants={item}>
          <MetricCard
            icon={<Globe className="h-6 w-6" />}
            title={t('dashboard.most_attacked_country')}
            value={isLoading ? undefined : data?.mostAttackedCountry}
            description={timeLabel}
            gradient="bg-gradient-to-br from-pink-500/20 to-red-500/20 hover:from-pink-500/30 hover:to-red-500/30 border-pink-500/50"
            textColor="text-pink-500"
            isText={true}
            linkTo={data?.mostAttackedCountry ? `/ransomware/${data.mostAttackedCountry.toLowerCase()}` : "/ransomware"}
          />
        </motion.div>
      </div>

      {/* Vulnerability Metrics Row (Restored) */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <motion.div variants={item}>
          <MetricCard
            icon={<AlertOctagon className="h-5 w-5" />}
            title={t('dashboard.critical_vulnerabilities')}
            value={isLoading ? undefined : data?.criticalVulnerabilitiesCount}
            description={timeLabel}
            gradient="bg-gradient-to-br from-red-600/20 to-red-800/20 hover:from-red-600/30 hover:to-red-800/30 border-red-600/50"
            textColor="text-red-500"
            linkTo="/cve"
          />
        </motion.div>
        <motion.div variants={item}>
          <MetricCard
            icon={<AlertCircle className="h-5 w-5" />}
            title={t('dashboard.high_vulnerabilities')}
            value={isLoading ? undefined : data?.highVulnerabilitiesCount}
            description={timeLabel}
            gradient="bg-gradient-to-br from-orange-500/20 to-amber-600/20 hover:from-orange-500/30 hover:to-amber-600/30 border-orange-500/50"
            textColor="text-orange-500"
            linkTo="/cve"
          />
        </motion.div>
        <motion.div variants={item}>
          <MetricCard
            icon={<AlertTriangle className="h-5 w-5" />}
            title={t('dashboard.medium_vulnerabilities')}
            value={isLoading ? undefined : data?.mediumVulnerabilitiesCount}
            description={timeLabel}
            gradient="bg-gradient-to-br from-yellow-400/20 to-amber-500/20 hover:from-yellow-400/30 hover:to-amber-500/30 border-yellow-400/50"
            textColor="text-yellow-500"
            linkTo="/cve"
          />
        </motion.div>
        <motion.div variants={item}>
          <MetricCard
            icon={<TrendingUp className="h-5 w-5" />}
            title={t('dashboard.low_vulnerabilities')}
            value={isLoading ? undefined : data?.lowVulnerabilitiesCount}
            description={timeLabel}
            gradient="bg-gradient-to-br from-emerald-400/20 to-teal-500/20 hover:from-emerald-400/30 hover:to-teal-500/30 border-emerald-400/50"
            textColor="text-emerald-500"
            linkTo="/cve"
          />
        </motion.div>
      </div>

      {/* Main Bento Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Map Widget - Spans 2 columns */}
        <motion.div variants={item} className="md:col-span-2 h-[450px]">
          <Suspense fallback={<Skeleton className="w-full h-full rounded-xl" />}>
            <RansomwareMapWidget data={data?.allRansomwareAttacks || []} isLoading={isLoading} days={days} />
          </Suspense>
        </motion.div>

        {/* Critical Vulnerabilities - Spans 1 column */}
        <motion.div variants={item} className="h-[450px]">
          <VulnerabilitiesCard vulnerabilities={data?.recentCriticalVulnerabilities || []} isLoading={isLoading} />
        </motion.div>

        {/* News - Spans 1 column */}
        <motion.div variants={item} className="h-[400px]">
          <NewsCard news={data?.latestNews || []} isLoading={isLoading} />
        </motion.div>

        {/* Ransomware Feed - Spans 1 column */}
        <motion.div variants={item} className="h-[400px]">
          <RansomwareCard ransomwareAttacks={data?.recentRansomwareAttacks || []} isLoading={isLoading} />
        </motion.div>

        {/* Leaks - Spans 1 column */}
        <motion.div variants={item} className="h-[400px]">
          <LeaksCard leaks={data?.recentLeaks || []} isLoading={isLoading} />
        </motion.div>
      </div>

      {/* Bottom Row - EOL and Releases */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <motion.div variants={item}>
          <EOLCard eolItems={data?.upcomingEOL || []} isLoading={isLoading} />
        </motion.div>
        <motion.div variants={item}>
          <ReleasesCard releases={data?.latestReleases || []} isLoading={isLoading} />
        </motion.div>
      </div>
    </motion.div>
  );
}
